import { useEffect, useRef } from 'react';

interface WebSocketHookProps {
  onOpen?: () => void;
  onMessage?: (data: any) => void;
  onError?: (error: any) => void;
  onClose?: () => void;
}

interface WebSocketHookReturn {
  sendMessage: (message: any) => void;
  sendBinaryMessage: (blob: Blob) => void;
  wsRef: React.MutableRefObject<WebSocket | null>;
  initWebSocket: () => void;
}

export const useWebSocket = ({
  onOpen,
  onMessage,
  onError,
  onClose
}: WebSocketHookProps): WebSocketHookReturn => {
  const wsRef = useRef<WebSocket | null>(null);

  const initWebSocket = () => {
    // No iniciar si ya hay una conexión activa
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    // Limpiar conexión existente
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${window.location.host}/entrevista`);
    
    ws.onopen = () => {
      onOpen?.();
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (!data.type) {
          throw new Error('Message type not specified');
        }
        
        if (!['status', 'text_chunk', 'analysis', 'error', 'response_complete', 'transcription', 'audio_chunk'].includes(data.type)) {
          throw new Error(`Tipo de mensaje no válido: ${data.type}`);
        }
        
        onMessage?.(data);
      } catch (error) {
        console.error('[WebSocket] Message parse error:', error);
        onError?.(error);
      }
    };

    ws.onerror = (error) => {
      console.error('[WebSocket] Error:', error);
      onError?.(error);
    };

    ws.onclose = (event) => {
      console.log('[WebSocket] Conexión cerrada:', event.code, event.reason);
      wsRef.current = null;
      onClose?.();
    };

    wsRef.current = ws;
  };

  const sendMessage = (message: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const messageStr = typeof message === 'string' ? message : JSON.stringify(message);
      wsRef.current.send(messageStr);
    } else {
      console.warn('[WebSocket] Cannot send message: connection not open');
    }
  };

  const sendBinaryMessage = async (blob: Blob) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        // Verificar que el blob no esté vacío
        if (blob.size === 0) {
          console.error('[WebSocket] Error: Blob vacío');
          return;
        }

        // Convertir el blob a base64 usando Promise
        const base64data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            if (typeof reader.result === 'string') {
              const base64 = reader.result.split(',')[1];
              if (!base64) {
                reject(new Error('Error al convertir audio a base64'));
                return;
              }
              resolve(base64);
            } else {
              reject(new Error('Error al leer el archivo de audio'));
            }
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });

        // Verificar que tengamos datos base64 válidos
        if (!base64data) {
          console.error('[WebSocket] Error: Datos base64 vacíos');
          return;
        }

        wsRef.current?.send(JSON.stringify({
          type: 'audio_input',
          audio: base64data
        }));
      } catch (error) {
        console.error('[WebSocket] Error al procesar audio:', error);
      }
    } else {
      console.warn('[WebSocket] Cannot send binary message: connection not open');
    }
  };

  // Cleanup al desmontar el componente
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  return {
    sendMessage,
    sendBinaryMessage,
    wsRef,
    initWebSocket
  };
};
